<template>
  <div class="grid grid-cols-12 items-center gap-3 mb-5">
    <div class="text-right col-span-3">
      {{ $t('customFields.adjustmentName') }}
    </div>
    <div class="col-span-9">
      <TextboxField :type="'text'" :name="'label'" v-model="customField.label" :isRequired="true" />
    </div>
  </div>

  <div class="grid grid-cols-12 items-center gap-3 mb-5">
    <div class="text-right col-span-3">
      {{ $t('customFields.min') }}
    </div>
    <div class="col-span-9">
      <div class="w-44">
        <InputOnlyNumber
          :min="0"
          :max="customField.maxValue"
          :name="'minValue'"
          v-model="customField.minValue"
          :controls="true"
        />
      </div>
    </div>
  </div>

  <div class="grid grid-cols-12 items-center gap-3 mb-5">
    <div class="text-right col-span-3">
      {{ $t('customFields.max') }}
    </div>
    <div class="col-span-9">
      <div class="w-44">
        <InputOnlyNumber
          :min="customField.minValue"
          :name="'maxValue'"
          v-model="customField.maxValue"
          :controls="true"
        />
      </div>
    </div>
  </div>

  <div class="grid grid-cols-12 items-center gap-3 mb-5">
    <div class="text-right col-span-3">
      {{ $t('customFields.precision') }}
    </div>
    <div class="col-span-9">
      <TextboxField :type="'text'" :name="'precision'" v-model="customField.precision" :isNumber="true" :min="0" />
    </div>
  </div>
  <div class="grid grid-cols-12 items-center gap-3 mb-5">
    <div class="text-right col-span-3">
      {{ $t('location.unit') }}
    </div>
    <div class="col-span-9">
      <div class="grid grid-cols-12 items-center">
        <div class="col-span-6">
          <div class="w-32">
            <TextboxField :type="'text'" :name="'unitType'" v-model="customField.unitType" />
          </div>
        </div>
        <div class="col-span-6">
          <el-select
            v-model="customField.position"
            :name="'position'"
            :placeholder="$t('barcode_type.position')"
            clearable
          >
            <el-option
              v-for="(componentName, key) in positionTypes"
              :key="key"
              :label="$t(`customFields.${key}`)"
              :value="key"
            >
              {{ $t(`customFields.${key}`) }}
            </el-option>
          </el-select>
        </div>
      </div>
    </div>
  </div>

  <!-- Attributes -->
  <div class="flex items-center gap-4">
    <div class="flex-grow font-bold">
      {{ $t('customFields.appearRoute') }}
    </div>
    <div>
      <el-button type="primary" icon="el-icon-plus" size="medium" @click="onAddRoute">
        {{ $t('barcode_type.add_new_route') }}
      </el-button>
    </div>
  </div>
  <div class="mb-4">
    <div class="flex items-center gap-4" :key="key" v-for="(route, key) in getTrackPointRoutes">
      <div class="flex items-center my-2 gap-4 justify-center">
        <div class="flex items-center justify-evenly">
          <el-select
            :placeholder="$t('barcode_type.select')"
            @change="onPathChange(route)"
            v-model="route.from"
            :key="route.from"
          >
            <el-option
              v-for="trackPoint in trackingPointDatasable()"
              :key="`${trackPoint?.value?.name} (${trackPoint?.key})`"
              :label="`${trackPoint?.value?.name} (${trackPoint?.key})`"
              :value="trackPoint?.key"
              :disabled="isDisableStartRoute(trackPoint)"
            />
          </el-select>
          <i class="el-icon-right mx-1"></i>
          <el-select :placeholder="$t('barcode_type.select')" class="mx-1" v-model="route.to" :key="route.to">
            <el-option
              v-for="item in getNextTrackpointsForm(route.from)"
              :disabled="isDisableEndRoute(item, route.from)"
              :key="`${item?.value?.name} (${item?.key})`"
              :label="`${item?.value?.name} (${item?.key})`"
              :value="item?.key"
            />
          </el-select>
          <div class="mx-1">
            <el-input class="w-full" v-model="route.customLabel" name="lastName" autocomplete="on" size="medium" />
          </div>
          <div>
            <el-button type="danger" @click="deleteRoutePath(key)" size="medium">
              <div class="hover:text-red-500">
                <IconTrash />
              </div>
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component'
import { InjectReactive, Prop, Watch } from 'vue-property-decorator'
import TextboxField from '@/components/validateFields/TextboxField.vue'
import InputOnlyNumber from '@/components/common/InputOnlyNumber.vue'
import IconTrash from '@/components/svg/IconTrash.vue'
import { IAdjustmentCustomFieldRoute, IProjectCustomField, ITrackPointForms, ITrackPointKeyVal } from '@/utils/types'
import { ECustomFieldUnitPosition } from '@/utils/enums'
import { isEmpty, objectToArray } from '@/utils/helpers'

@Options({
  components: { TextboxField, InputOnlyNumber, IconTrash },
  name: 'Number',
})
export default class Number extends Vue {
  @InjectReactive() trackingPointDatasArr!: ITrackPointKeyVal[]
  @Prop({ type: Object }) readonly customField!: IProjectCustomField
  _trackPointRoutePath!: IAdjustmentCustomFieldRoute[]

  get positionTypes(): Record<string, string> {
    return {
      [ECustomFieldUnitPosition.PREFIX]: 'prefix',
      [ECustomFieldUnitPosition.SUFFIX]: 'suffix',
    }
  }

  get getTrackPointRoutes(): IAdjustmentCustomFieldRoute[] {
    return this.customField?.trackPointRoutes || []
  }

  @Watch('trackingPointDatasArr')
  trackingPointDatasable() {
    const ableArray = this.trackingPointDatasArr.filter((val) => {
      return !val.value.isEnd
    })

    return ableArray
  }

  @Watch('trackingPointDatasArr')
  getNextTrackpointsForm(key: string) {
    return objectToArray<ITrackPointForms>(
      this.trackingPointDatasArr.find((tp) => tp.key === key)?.value.trackPointForms || {}
    ).map((val) => ({
      key: val.key,
      value: {
        ...val.value,
        name: this.trackingPointDatasArr.find((tp) => tp.key === val.key)?.value.name,
      },
    }))
  }

  mounted() {
    if (this.customField.position === ECustomFieldUnitPosition.UNKNOWN) {
      this.customField.position = ECustomFieldUnitPosition.DEFAULT
    }
  }

  onPathChange(route: IAdjustmentCustomFieldRoute) {
    route.to = this.getEndRouteAvailable(route.from)?.[0]?.key || ''
  }

  getEndRouteAvailable(startedKey: string) {
    const found = this.getTrackPointRoutes.filter((v) => v.from === startedKey)
    return this.getNextTrackpointsForm(startedKey).filter((o) => !found.find((v) => v.to === o.key))
  }

  isDisableStartRoute(trackpoint: ITrackPointKeyVal) {
    return isEmpty(this.getEndRouteAvailable(trackpoint.key))
  }

  isDisableEndRoute(tp: ITrackPointKeyVal, startedRoute: string) {
    return !!this.customField?.trackPointRoutes?.find((v) => v.from === startedRoute && v.to === tp.key)
  }

  deleteRoutePath(idx: number) {
    if (this.customField?.trackPointRoutes) {
      this.customField.trackPointRoutes.splice(idx, 1)
    }
  }

  onAddRoute() {
    this.customField.trackPointRoutes = this.customField.trackPointRoutes ?? []
    this.customField.trackPointRoutes.push({
      from: '',
      to: '',
      customLabel: '',
    })
  }
}
</script>
